import {
  createAsyncAction,
  ActionType,
  createReducer,
  createAction,
} from "typesafe-actions";
import {
  EmployeeDto,
  ListItemDto,
  ResultOfEmployeeSaveResponse,
  EmployeeSaveRequest,
  ResultOfBoolean,
  GetPositionsAutocompleteQuery,
  EmployeeAutocompleteDto,
  GetEmployeesAutocompleteQuery,
  EmployeeTypeListDto,
} from "Api/Api";

export type HumanResourcesState = {
  openedEmployeeIDs: number[] | undefined;
  isEmployeesListLoading: boolean;
  employeeDetail: EmployeeDto | null;
  isEmployeeDetailLoading: boolean;
  wasEmployeeSaved: boolean;
  employeesAutocompleteList: EmployeeAutocompleteDto[] | null;
  isEmployeesAutocompleteLoading: boolean;
  positionsAutocompleteList: ListItemDto[] | null;
  isPositionsAutocompleteLoading: boolean;
  departmentsAutocompleteList: ListItemDto[] | null;
  isDepartmentsAutocompleteLoading: boolean;
  companyAutocompleteList: ListItemDto[] | null;
  employeeTypeList: EmployeeTypeListDto[] | null;
  isEmployeeTypeListLoading: boolean;
  isCompanyAutocompleteLoading: boolean;
  isEmployeeDetailSaving: boolean;
  isEmployeeDetailDeleting: boolean;
  deletedEmployeeIDs: number[];
};

export const getHumanResourcesEmployeeDetailAsync = createAsyncAction(
  "@human_resources/GET_EMPLOYEE_DETAIL_REQUEST",
  "@human_resources/GET_EMPLOYEE_DETAIL_SUCCESS",
  "@human_resources/GET_EMPLOYEE_DETAIL_FAILURE",
)<number, EmployeeDto, Error>();

export const toggleEmployeeWasSaved = createAction(
  "@human_resources/TOGGLE_EMPLOYEE_SAVED",
)<void>();

export const saveHumanResourcesEmployeeAsync = createAsyncAction(
  "@human_resources/SAVE_EMPLOYEE_REQUEST",
  "@human_resources/SAVE_EMPLOYEE_SUCCESS",
  "@human_resources/SAVE_EMPLOYEE_FAILURE",
)<EmployeeSaveRequest, ResultOfEmployeeSaveResponse, Error>();

export const deleteHumanResourcesEmployeeAsync = createAsyncAction(
  "@human_resources/DELETE_EMPLOYEE_REQUEST",
  "@human_resources/DELETE_EMPLOYEE_SUCCESS",
  "@human_resources/DELETE_EMPLOYEE_FAILURE",
)<number, { result: ResultOfBoolean; employeeID: number }, Error>();

export const resetHumanResourcesEmployeeDetail = createAction(
  "@human_resources/RESET_EMPLOYEE_DETAIL",
)<void>();

export const setOpenedEmployeeIDs = createAction(
  "@human_resources/SET_OPENED_EMPLOYEES",
)<number[]>();

export const getHumanResourcesEmployeeAutocompleteAsync = createAsyncAction(
  "@human_resources/GET_EMPLOYEE_AUTOCOMPLETE_REQUEST",
  "@human_resources/GET_EMPLOYEE_AUTOCOMPLETE_SUCCESS",
  "@human_resources/GET_EMPLOYEE_AUTOCOMPLETE_FAILURE",
)<GetEmployeesAutocompleteQuery, EmployeeAutocompleteDto[], Error>();

export const getHumanResourcesPositionsAutocompleteAsync = createAsyncAction(
  "@human_resources/GET_POSITIONS_AUTOCOMPLETE_REQUEST",
  "@human_resources/GET_POSITIONS_AUTOCOMPLETE_SUCCESS",
  "@human_resources/GET_POSITIONS_AUTOCOMPLETE_FAILURE",
)<GetPositionsAutocompleteQuery, ListItemDto[], Error>();

export const getHumanResourcesDepartmentsAutocompleteAsync = createAsyncAction(
  "@human_resources/GET_DEPARTMENTS_AUTOCOMPLETE_REQUEST",
  "@human_resources/GET_DEPARTMENTS_AUTOCOMPLETE_SUCCESS",
  "@human_resources/GET_DEPARTMENTS_AUTOCOMPLETE_FAILURE",
)<string, ListItemDto[], Error>();

export const getHumanResourcesCompanyAutocompleteAsync = createAsyncAction(
  "@human_resources/GET_COMPANY_AUTOCOMPLETE_REQUEST",
  "@human_resources/GET_COMPANY_AUTOCOMPLETE_SUCCESS",
  "@human_resources/GET_COMPANY_AUTOCOMPLETE_FAILURE",
)<string, ListItemDto[], Error>();

export const getHumanResourcesEmployeeTypeListAsync = createAsyncAction(
  "@human_resources/GET_EMPLOYEE_TYPE_LIST_REQUEST",
  "@human_resources/GET_EMPLOYEE_TYPE_LIST_SUCCESS",
  "@human_resources/GET_EMPLOYEE_TYPE_LIST_FAILURE",
)<void, EmployeeTypeListDto[], Error>();

export type HumanResourcesAction =
  | ActionType<typeof getHumanResourcesEmployeeDetailAsync>
  | ActionType<typeof resetHumanResourcesEmployeeDetail>
  | ActionType<typeof getHumanResourcesEmployeeAutocompleteAsync>
  | ActionType<typeof getHumanResourcesPositionsAutocompleteAsync>
  | ActionType<typeof getHumanResourcesDepartmentsAutocompleteAsync>
  | ActionType<typeof getHumanResourcesCompanyAutocompleteAsync>
  | ActionType<typeof saveHumanResourcesEmployeeAsync>
  | ActionType<typeof deleteHumanResourcesEmployeeAsync>
  | ActionType<typeof getHumanResourcesEmployeeTypeListAsync>
  | ActionType<typeof toggleEmployeeWasSaved>
  | ActionType<typeof setOpenedEmployeeIDs>;

export const humanResourcesReducer = createReducer<
  HumanResourcesState,
  HumanResourcesAction
>({
  isEmployeesListLoading: false,
  employeeDetail: null,
  isEmployeeDetailLoading: false,
  employeesAutocompleteList: null,
  isEmployeesAutocompleteLoading: false,
  positionsAutocompleteList: null,
  isPositionsAutocompleteLoading: false,
  departmentsAutocompleteList: null,
  isDepartmentsAutocompleteLoading: false,
  companyAutocompleteList: null,
  isCompanyAutocompleteLoading: false,
  isEmployeeDetailSaving: false,
  isEmployeeDetailDeleting: false,
  deletedEmployeeIDs: [],
  wasEmployeeSaved: false,
  openedEmployeeIDs: [],
  employeeTypeList: null,
  isEmployeeTypeListLoading: false,
})
  .handleAction(getHumanResourcesEmployeeDetailAsync.request, state => {
    return {
      ...state,
      isEmployeeDetailLoading: true,
    };
  })
  .handleAction(
    getHumanResourcesEmployeeDetailAsync.success,
    (state, action) => {
      return {
        ...state,
        isEmployeeDetailLoading: false,
        employeeDetail: action.payload,
      };
    },
  )
  .handleAction(getHumanResourcesEmployeeDetailAsync.failure, state => {
    return {
      ...state,
      isEmployeeDetailLoading: false,
    };
  })
  .handleAction(setOpenedEmployeeIDs, (state, action) => {
    return {
      ...state,
      openedEmployeeIDs: action.payload,
    };
  })
  .handleAction(resetHumanResourcesEmployeeDetail, state => {
    return {
      ...state,
      employeeDetail: null,
    };
  })
  .handleAction(toggleEmployeeWasSaved, state => {
    return {
      ...state,
      wasEmployeeSaved: false,
    };
  })
  .handleAction(getHumanResourcesEmployeeAutocompleteAsync.request, state => {
    return {
      ...state,
      isEmployeesAutocompleteLoading: true,
    };
  })
  .handleAction(
    getHumanResourcesEmployeeAutocompleteAsync.success,
    (state, action) => {
      return {
        ...state,
        isEmployeesAutocompleteLoading: false,
        employeesAutocompleteList: action.payload,
      };
    },
  )
  .handleAction(getHumanResourcesEmployeeAutocompleteAsync.failure, state => {
    return {
      ...state,
      isEmployeesAutocompleteLoading: false,
    };
  })

  .handleAction(getHumanResourcesPositionsAutocompleteAsync.request, state => {
    return {
      ...state,
      isPositionsAutocompleteLoading: true,
    };
  })
  .handleAction(
    getHumanResourcesPositionsAutocompleteAsync.success,
    (state, action) => {
      return {
        ...state,
        isPositionsAutocompleteLoading: false,
        positionsAutocompleteList: action.payload,
      };
    },
  )
  .handleAction(getHumanResourcesPositionsAutocompleteAsync.failure, state => {
    return {
      ...state,
      isPositionsAutocompleteLoading: false,
    };
  })

  .handleAction(
    getHumanResourcesDepartmentsAutocompleteAsync.request,
    state => {
      return {
        ...state,
        isDepartmentsAutocompleteLoading: true,
      };
    },
  )
  .handleAction(
    getHumanResourcesDepartmentsAutocompleteAsync.success,
    (state, action) => {
      return {
        ...state,
        isDepartmentsAutocompleteLoading: false,
        departmentsAutocompleteList: action.payload,
      };
    },
  )
  .handleAction(
    getHumanResourcesDepartmentsAutocompleteAsync.failure,
    state => {
      return {
        ...state,
        isDepartmentsAutocompleteLoading: false,
      };
    },
  )

  .handleAction(getHumanResourcesCompanyAutocompleteAsync.request, state => {
    return {
      ...state,
      isCompanyAutocompleteLoading: true,
    };
  })
  .handleAction(
    getHumanResourcesCompanyAutocompleteAsync.success,
    (state, action) => {
      return {
        ...state,
        isCompanyAutocompleteLoading: false,
        companyAutocompleteList: action.payload,
      };
    },
  )
  .handleAction(getHumanResourcesCompanyAutocompleteAsync.failure, state => {
    return {
      ...state,
      isCompanyAutocompleteLoading: false,
    };
  })

  .handleAction(saveHumanResourcesEmployeeAsync.request, state => {
    return {
      ...state,
      isEmployeeDetailSaving: true,
    };
  })
  .handleAction(saveHumanResourcesEmployeeAsync.success, (state, action) => {
    return {
      ...state,
      isEmployeeDetailSaving: false,
      wasEmployeeSaved: true,
      employeeDetail: action.payload.data?.employee ?? null,
    };
  })
  .handleAction(saveHumanResourcesEmployeeAsync.failure, state => {
    return {
      ...state,
      isEmployeeDetailSaving: false,
    };
  })

  .handleAction(deleteHumanResourcesEmployeeAsync.request, state => {
    return {
      ...state,
      isEmployeeDetailDeleting: true,
    };
  })
  .handleAction(deleteHumanResourcesEmployeeAsync.success, (state, action) => {
    if (!action.payload.result.isValid) {
      return {
        ...state,
      };
    }

    return {
      ...state,
      isEmployeeDetailDeleting: false,
      employeeDetail: null,
      deletedEmployeeIDs: [
        ...state.deletedEmployeeIDs,
        action.payload.employeeID,
      ],
    };
  })
  .handleAction(deleteHumanResourcesEmployeeAsync.failure, state => {
    return {
      ...state,
      isEmployeeDetailDeleting: false,
    };
  })

  .handleAction(getHumanResourcesEmployeeTypeListAsync.request, state => {
    return {
      ...state,
      isEmployeeTypeListLoading: true,
    };
  })
  .handleAction(
    getHumanResourcesEmployeeTypeListAsync.success,
    (state, action) => {
      return {
        ...state,
        isEmployeeTypeListLoading: false,
        employeeTypeList: action.payload,
      };
    },
  )
  .handleAction(getHumanResourcesEmployeeTypeListAsync.failure, state => {
    return {
      ...state,
      isEmployeeTypeListLoading: false,
    };
  });
